import React, { FunctionComponent, useContext } from 'react'
import { defineMessage, defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { useTheme } from 'styled-components/native'

import {
  computeRemainderSessions,
  isUnlimitedSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { AuroraAppSpotIllustration } from '../../../atoms/illustrations/AuroraAppSpotIllustration'
import { AuroraCareNavigatorSpotIllustration } from '../../../atoms/illustrations/AuroraCareNavigatorSpotIllustration'
import { AuroraCareSessionsSpotIllustration } from '../../../atoms/illustrations/AuroraCareSessionsSpotIllustration'
import { AuroraNeurodiversitySpotIllustration } from '../../../atoms/illustrations/AuroraNeurodiversitySpotIllustration'
import { AuroraSelfCareResourcesSpotIllustration } from '../../../atoms/illustrations/AuroraSelfCareResourcesSpotIllustration'
import { CareNavigatorValuePropSpotIllustration } from '../../../atoms/illustrations/CareNavigatorValuePropSpotIllustration'
import { IntelligentMatchingValuePropSpotIllustration } from '../../../atoms/illustrations/IntelligentMatchingValuePropSpotIllustration'
import { LyraSelectCareNavigatorValuePropSpotIllustration } from '../../../atoms/illustrations/LyraSelectCareNavigatorValuePropSpotIllustration'
import { LyraSelectCareSessionIllustration } from '../../../atoms/illustrations/LyraSelectCareSessionIllustration'
import { LyraSelectPersonalizedCareIllustration } from '../../../atoms/illustrations/LyraSelectPersonalizedCareIllustration'
import { LyraSelectSelfCareValueSpotIllustration } from '../../../atoms/illustrations/LyraSelectSelfCareValueSpotIllustration'
import { SelfCareValuePropSpotIllustration } from '../../../atoms/illustrations/SelfCareValuePropSpotIllustration'
import { SessionsValuePropSpotIllustration } from '../../../atoms/illustrations/SessionsValuePropSpotIllustration'
import { AppContext } from '../../../context'
import { MembershipSummary } from '../../../molecules/membershipSummary/MembershipSummary'
import { CARE_OFFERING_DESCRIPTION_TYPE } from '../../../utils/costMessaging/utils'
import { ProductTypeContext } from '../../../utils/productType/productTypeContext'
import { ThemeType } from '../../../utils/themes/ThemeProvider'

export interface LyraMembershipSummaryProps {
  customerDisplayName?: string
  numberOfSessions?: string
  displayVisitsPerIssuePerYear?: number
  numberOfSessionsByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  careOfferingDescriptionType?: CARE_OFFERING_DESCRIPTION_TYPE
  isLibraryDisabled?: boolean
  isDependentDisabled?: boolean
  backgroundColor?: string
}

enum LyraMemberOfferings {
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS',
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_FAMILY = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_FAMILY',
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP',
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_FAMILY = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_FAMILY',
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_HPI = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_HPI',
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI',
  ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI_CHILDREN_DISABLED = 'ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI_CHILDREN_DISABLED',
  ONE_ON_ONE_NEURODIVERSITY_SESSIONS = 'ONE_ON_ONE_NEURODIVERSITY_SESSIONS',
  ONE_ON_ONE_NEURODIVERSITY_SESSIONS_FAMILY = 'ONE_ON_ONE_NEURODIVERSITY_SESSIONS_FAMILY',
  SELF_GUIDED_WELLNESS_RESOURCES = 'SELF_GUIDED_WELLNESS_RESOURCES',
  DEDICATED_SUPPORT = 'DEDICATED_SUPPORT',
  CARE_MATCHED_TO_YOU = 'CARE_MATCHED_TO_YOU',
  LYRA_SELECT_SELF_GUIDED_WELLNESS_RESOURCES = 'LYRA_SELECT_SELF_GUIDED_WELLNESS_RESOURCES',
  LYRA_SELECT_PERSONALIZED_CARE = 'LYRA_SELECT_PERSONALIZED_CARE',
  LYRA_SELECT_DEDICATED_SUPPORT = 'LYRA_SELECT_DEDICATED_SUPPORT',
  LYRA_SELECT_ONE_ON_ONE_MENTAL_HEALTH_SESSIONS = 'LYRA_SELECT_ONE_ON_ONE_MENTAL_HEALTH_SESSIONS',
}

const lyraMemberOfferingsLabels = defineMessages({
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS]: {
    defaultMessage: '1-on-1 mental health sessions',
    description: 'Label indicating members of Lyra will receive personal mental health sessions',
  },
  [LyraMemberOfferings.SELF_GUIDED_WELLNESS_RESOURCES]: {
    defaultMessage: 'Self-guided wellness resources',
    description: 'Label indicating members of Lyra will receive access to self guided mental health resources',
  },
  [LyraMemberOfferings.DEDICATED_SUPPORT]: {
    defaultMessage: 'Dedicated support, anytime',
    description: 'Label indicating members of Lyra will have access to personal support',
  },
  [LyraMemberOfferings.CARE_MATCHED_TO_YOU]: {
    defaultMessage: 'Care matched to you',
    description: 'Label indicating members of Lyra will be matched to providers personalized to their needs',
  },
  [LyraMemberOfferings.LYRA_SELECT_SELF_GUIDED_WELLNESS_RESOURCES]: {
    defaultMessage: 'Self-care resources',
    description:
      'Lyra select label indicating members of Lyra will receive access to self guided mental health resources',
  },
  [LyraMemberOfferings.LYRA_SELECT_PERSONALIZED_CARE]: {
    defaultMessage: 'Personalized care',
    description:
      'Lyra select label indicating members of Lyra will be matched to providers personalized to their needs',
  },
  [LyraMemberOfferings.LYRA_SELECT_DEDICATED_SUPPORT]: {
    defaultMessage: 'Get in touch, anytime',
    description: 'Lyra select label indicating members of Lyra will have access to personal support',
  },
  [LyraMemberOfferings.LYRA_SELECT_ONE_ON_ONE_MENTAL_HEALTH_SESSIONS]: {
    defaultMessage: '1-on-1 care sessions',
    description: 'Lyra select label indicating members of Lyra will receive personal mental health sessions',
  },
  [LyraMemberOfferings.ONE_ON_ONE_NEURODIVERSITY_SESSIONS]: {
    defaultMessage: 'Support for neurodiversity',
    description: 'Label indicating members of Lyra will receive personal neurodiversity sessions',
  },
})

const lyraMemberOfferingsDescriptions = defineMessages({
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS]: {
    defaultMessage: 'Get personalized care when you need support.',
    description: 'A description indicating that members of Lyra will receive mental health sessions',
  },
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_FAMILY]: {
    defaultMessage: 'Get personalized care when you need support—for yourself and your family.',
    description: 'A description indicating that members of Lyra and their families will receive mental health sessions',
  },
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP]: {
    defaultMessage:
      '{hasDisplayVisitsPerIssuePerYear, select, true {' +
      '{displayVisitsPerIssuePerYear} sessions per issue, per year' +
      '} other {' +
      '{showPerIssuePerYearText, select, true {' +
      '{hasTherapyPerIssuePerYear, select, true {' +
      '{hasCoachingPerIssuePerYear, select, true {' +
      '{therapyPerIssuePerYear} therapy and {coachingPerIssuePerYear} coaching sessions per issue, per year' +
      '} other {' +
      '{therapyPerIssuePerYear} therapy sessions per issue, per year and {isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '}}' +
      '} other {' +
      '{hasCoachingPerIssuePerYear, select, true {' +
      '{numberOfTherapySessions} therapy sessions per year and {coachingPerIssuePerYear} coaching sessions per issue, per year' +
      '} other {' +
      '{numberOfTherapySessions} therapy and {isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '}}' +
      '}}' +
      '} other {' +
      '{showProgramLevelSessionText, select, true {' +
      '{numberOfTherapySessions} therapy and ' +
      '{isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '} other {' +
      '{numberOfSessions} included sessions per year' +
      '}}' +
      '}}' +
      '}}' +
      ' when you need support.',
    description:
      'A description indicating that members of Lyra will receive a certain amount of mental health sessions',
  },
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_FAMILY]: {
    defaultMessage:
      '{hasDisplayVisitsPerIssuePerYear, select, true {' +
      '{displayVisitsPerIssuePerYear} sessions per issue, per year' +
      '} other {' +
      '{showPerIssuePerYearText, select, true {' +
      '{hasTherapyPerIssuePerYear, select, true {' +
      '{hasCoachingPerIssuePerYear, select, true {' +
      '{therapyPerIssuePerYear} therapy and {coachingPerIssuePerYear} coaching sessions per issue, per year' +
      '} other {' +
      '{therapyPerIssuePerYear} therapy sessions per issue, per year and {isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '}}' +
      '} other {' +
      '{hasCoachingPerIssuePerYear, select, true {' +
      '{numberOfTherapySessions} therapy sessions per year and {coachingPerIssuePerYear} coaching sessions per issue, per year' +
      '} other {' +
      '{numberOfTherapySessions} therapy and {isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '}}' +
      '}}' +
      '} other {' +
      '{showProgramLevelSessionText, select, true {' +
      '{numberOfTherapySessions} therapy and ' +
      '{isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '} other {' +
      '{numberOfSessions} included sessions per year' +
      '}}' +
      '}}' +
      '}}' +
      ' when you need support—for yourself and your family.',
    description:
      'A description indicating that members of Lyra and their families will receive a certain amount of mental health sessions',
  },
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_HPI]: {
    defaultMessage:
      '{hasDisplayVisitsPerIssuePerYear, select, true {' +
      '{displayVisitsPerIssuePerYear} sessions per issue, per year' +
      '} other {' +
      '{showPerIssuePerYearText, select, true {' +
      '{hasTherapyPerIssuePerYear, select, true {' +
      '{hasCoachingPerIssuePerYear, select, true {' +
      '{therapyPerIssuePerYear} therapy and {coachingPerIssuePerYear} coaching sessions per issue, per year' +
      '} other {' +
      '{therapyPerIssuePerYear} therapy sessions per issue, per year and {isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '}}' +
      '} other {' +
      '{hasCoachingPerIssuePerYear, select, true {' +
      '{numberOfTherapySessions} therapy sessions per year and {coachingPerIssuePerYear} coaching sessions per issue, per year' +
      '} other {' +
      '{numberOfTherapySessions} therapy and {isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '}}' +
      '}}' +
      '} other {' +
      '{showProgramLevelSessionText, select, true {' +
      '{numberOfTherapySessions} therapy and ' +
      '{isUnlimitedCoaching, select, true {unlimited} other {{numberOfCoachingSessions}}} coaching sessions per year' +
      '} other {' +
      '{numberOfSessions} included sessions per year' +
      '}}' +
      '}}' +
      '}}' +
      ', then check with your health plan for additional coverage.',
    description:
      'A description indicating that members of Lyra will receive a certain amount of mental health sessions, then additional coverage through a health plan',
  },
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI]: {
    defaultMessage: 'Get personalized care through an eligible health plan for you, your partner, or your children.',
    description:
      'A description indicating that members of Lyra, their partner, and children will receive mental health sessions through their health plan coverage',
  },
  [LyraMemberOfferings.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI_CHILDREN_DISABLED]: {
    defaultMessage: 'Get personalized care through an eligible health plan for you and your loved ones.',
    description:
      'A description indicating that members of Lyra will receive mental health sessions through their health plan coverage',
  },
  [LyraMemberOfferings.SELF_GUIDED_WELLNESS_RESOURCES]: {
    defaultMessage: 'Browse meditations, courses, articles and more at your own pace.',
    description: 'A description describing the kinds of self guided resources available to the member',
  },
  [LyraMemberOfferings.DEDICATED_SUPPORT]: {
    defaultMessage: 'Reach out, day or night, for personal guidance that’s private and confidential.',
    description: 'A description indicating user can reach out at any time for private and personal support',
  },
  [LyraMemberOfferings.CARE_MATCHED_TO_YOU]: {
    defaultMessage: 'Providers intelligently matched to your needs and background.',
    description: 'A description describing how providers will be personally matched based on the needs of the member',
  },
  [LyraMemberOfferings.LYRA_SELECT_SELF_GUIDED_WELLNESS_RESOURCES]: {
    defaultMessage:
      'Browse on-demand meditations, exercises, and articles to help with stress, sleep, burnout, and more.',
    description: 'Lyra select description describing the kinds of self guided resources available to the member',
  },
  [LyraMemberOfferings.LYRA_SELECT_PERSONALIZED_CARE]: {
    defaultMessage: 'Tell us about your challenges and goals, and we’ll provide curated resources just for you.',
    description:
      "Lyra select description describing how providers will be personally matched based on the needs of the member'",
  },
  [LyraMemberOfferings.LYRA_SELECT_DEDICATED_SUPPORT]: {
    defaultMessage: 'Reach out, day or night, for personal guidance that’s private and confidential.',
    description: 'Lyra select description indicating user can reach out at any time for private and personal support',
  },
  [LyraMemberOfferings.ONE_ON_ONE_NEURODIVERSITY_SESSIONS]: {
    defaultMessage:
      'Get {isUnlimited, select, true {unlimited sessions} other {up to {numberOfNeurodiversitySessions} sessions}} per year covered by {customerDisplayName} for yourself.',
    description: 'A description indicating that members of Lyra will receive neurodiversity sessions',
  },
  [LyraMemberOfferings.ONE_ON_ONE_NEURODIVERSITY_SESSIONS_FAMILY]: {
    defaultMessage:
      'Get {isUnlimited, select, true {unlimited sessions} other {up to {numberOfNeurodiversitySessions} sessions}} per year covered by {customerDisplayName} for yourself and your family.',
    description:
      'A description indicating that members of Lyra will receive neurodiversity sessions for themselves and their family',
  },
})

const LYRA_MEMBERSHIP_SECTION_TITLE = defineMessage({
  defaultMessage: `What you get with Lyra`,
  description: 'Title for the lyra membership offerings section.',
})

export const LyraMembershipSummary: FunctionComponent<LyraMembershipSummaryProps> = ({
  customerDisplayName,
  careOfferingDescriptionType,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  isLibraryDisabled,
  isDependentDisabled,
  backgroundColor,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { breakpoints, isAuroraTheme } = useTheme() as ThemeType
  const { isMobileSized } = breakpoints
  const { isLyraSelect } = useContext(ProductTypeContext)
  const { isCustomerDynamicSessionLimitEnabled, isHideCostAndSessionLimitEnabled } = useContext(AppContext)
  const iconSize = isMobileSized ? 64 : 96
  const numberOfCoachingSessions = numberOfSessionsByProgram?.coaching
  const numberOfTherapySessions = numberOfSessionsByProgram?.therapy
  const numberOfNeurodiversitySessions = numberOfSessionsByProgram?.neurodiversity
  const numberOfSessions = computeRemainderSessions(
    parseInt(props.numberOfSessions ?? '0'),
    numberOfSessionsByProgram,
  ).toString()

  // Determine which programs have per-issue-per-year limits
  const therapyPerIssuePerYear = displayVisitsPerIssuePerYearByProgram?.therapy
  const coachingPerIssuePerYear = displayVisitsPerIssuePerYearByProgram?.coaching
  const hasTherapyPerIssuePerYear = !!therapyPerIssuePerYear
  const hasCoachingPerIssuePerYear = !!coachingPerIssuePerYear

  const coachingAndTherapyCountMessage = (
    <FormattedMessage
      defaultMessage='When you need extra support, you get {numberOfCoachingSessions} coaching and {numberOfTherapySessions} therapy sessions—paid for by {customerDisplayName}, at no cost to you.'
      description='Lyra select description indicating that members of Lyra will receive mental health sessions'
      values={{
        customerDisplayName,
        numberOfCoachingSessions: numberOfCoachingSessions ?? 0,
        numberOfTherapySessions: numberOfTherapySessions ?? 0,
      }}
    />
  )

  const totalNumberOfSessionMessage = (
    <FormattedMessage
      defaultMessage='When you need extra support, you get {numberOfSessions} therapy sessions—paid for by {customerDisplayName}, at no cost to you.'
      description='Lyra select description indicating that members of Lyra will receive mental health sessions'
      values={{ numberOfSessions: numberOfSessions, customerDisplayName }}
    />
  )

  const totalDisplayVisitsPerIssuePerYearMessage = (
    <FormattedMessage
      defaultMessage='When you need extra support, you get {displayVisitsPerIssuePerYear} sessions per issue, per year—paid for by {customerDisplayName}, at no cost to you.'
      description='Lyra select description indicating that members of Lyra will receive mental health sessions'
      values={{ displayVisitsPerIssuePerYear, customerDisplayName }}
    />
  )

  // Per-issue-per-year messages for Lyra Select
  const bothPerIssuePerYearMessage = (
    <FormattedMessage
      defaultMessage='When you need extra support, you get {therapyPerIssuePerYear} therapy and {coachingPerIssuePerYear} coaching sessions per issue, per year—paid for by {customerDisplayName}, at no cost to you.'
      description='Lyra select description for both therapy and coaching per issue per year'
      values={{
        customerDisplayName,
        therapyPerIssuePerYear,
        coachingPerIssuePerYear,
      }}
    />
  )

  const therapyPerIssueCoachingPerYearMessage = (
    <FormattedMessage
      defaultMessage='When you need extra support, you get {therapyPerIssuePerYear} therapy sessions per issue, per year and {numberOfCoachingSessions} coaching sessions per year—paid for by {customerDisplayName}, at no cost to you.'
      description='Lyra select description for therapy per issue per year and coaching per year'
      values={{
        customerDisplayName,
        therapyPerIssuePerYear,
        numberOfCoachingSessions,
      }}
    />
  )

  const coachingPerIssueTherapyPerYearMessage = (
    <FormattedMessage
      defaultMessage='When you need extra support, you get {numberOfTherapySessions} therapy sessions per year and {coachingPerIssuePerYear} coaching sessions per issue, per year—paid for by {customerDisplayName}, at no cost to you.'
      description='Lyra select description for therapy per year and coaching per issue per year'
      values={{
        customerDisplayName,
        numberOfTherapySessions,
        coachingPerIssuePerYear,
      }}
    />
  )

  // Generic message for dynamic session limits
  const genericSessionMessage = (
    <FormattedMessage
      defaultMessage='Meet with a provider when you need support—for yourself and your family.'
      description='Generic description for dynamic session limits without specific session counts'
    />
  )

  const getMemberCareOfferingDescription = (careOfferingDescriptionType?: CARE_OFFERING_DESCRIPTION_TYPE) => {
    if (isHideCostAndSessionLimitEnabled) {
      return {
        text: formatMessage(
          isDependentDisabled
            ? lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS
            : lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_FAMILY,
        ),
        testID: 'careSection-description-default',
      }
    }

    if (isCustomerDynamicSessionLimitEnabled) {
      return {
        text: genericSessionMessage,
        testID: 'careSection-description-generic',
      }
    }

    let selectedDescription
    switch (careOfferingDescriptionType) {
      case CARE_OFFERING_DESCRIPTION_TYPE.EAP_ONLY_DEPENDENT_DISABLED:
        selectedDescription = lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP
        break
      case CARE_OFFERING_DESCRIPTION_TYPE.EAP_AND_HPI:
        selectedDescription = lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_HPI
        break
      case CARE_OFFERING_DESCRIPTION_TYPE.EAP_ONLY:
        selectedDescription = lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_EAP_FAMILY
        break
      case CARE_OFFERING_DESCRIPTION_TYPE.HPI_ONLY:
        selectedDescription = lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI
        break
      case CARE_OFFERING_DESCRIPTION_TYPE.HPI_ONLY_CHILDREN_DISABLED:
        selectedDescription = lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_HPI_CHILDREN_DISABLED
        break
      default:
        selectedDescription = isDependentDisabled
          ? lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS
          : lyraMemberOfferingsDescriptions.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS_FAMILY
        break
    }

    return {
      text: formatMessage(selectedDescription, {
        customerDisplayName,
        numberOfSessions: numberOfSessions,
        hasDisplayVisitsPerIssuePerYear: !!displayVisitsPerIssuePerYear,
        displayVisitsPerIssuePerYear,
        showProgramLevelSessionText: !!numberOfSessionsByProgram?.coaching && !!numberOfSessionsByProgram?.therapy,
        showPerIssuePerYearText: hasTherapyPerIssuePerYear || hasCoachingPerIssuePerYear,
        hasTherapyPerIssuePerYear,
        hasCoachingPerIssuePerYear,
        therapyPerIssuePerYear,
        coachingPerIssuePerYear,
        numberOfTherapySessions: numberOfTherapySessions,
        numberOfCoachingSessions: numberOfCoachingSessions,
        isUnlimitedCoaching: isUnlimitedSessionLimit(numberOfSessionsByProgram?.coaching ?? 0),
      }),
      testID: careOfferingDescriptionType
        ? `careSection-description-${careOfferingDescriptionType}`
        : 'careSection-description-default',
    }
  }

  const memberOfferings = [
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels.ONE_ON_ONE_MENTAL_HEALTH_SESSIONS),
        testID: 'careSection-label',
      },
      description: getMemberCareOfferingDescription(careOfferingDescriptionType),
      icon: isAuroraTheme ? (
        <AuroraCareSessionsSpotIllustration size={iconSize} />
      ) : (
        <SessionsValuePropSpotIllustration size={iconSize} />
      ),
      itemTestID: 'careSection',
    },
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels.ONE_ON_ONE_NEURODIVERSITY_SESSIONS),
        testID: 'neurodiversitySessions-label',
      },
      description: {
        text: formatMessage(
          isDependentDisabled
            ? lyraMemberOfferingsDescriptions.ONE_ON_ONE_NEURODIVERSITY_SESSIONS
            : lyraMemberOfferingsDescriptions.ONE_ON_ONE_NEURODIVERSITY_SESSIONS_FAMILY,
          {
            customerDisplayName,
            numberOfNeurodiversitySessions,
            isUnlimited: isUnlimitedSessionLimit(numberOfNeurodiversitySessions ?? 0),
          },
        ),
        testID: 'neurodiversitySessions-description',
      },
      icon: <AuroraNeurodiversitySpotIllustration size={iconSize} />,
      itemTestID: 'neurodiversitySessions',
      hide: !numberOfNeurodiversitySessions,
    },
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels.SELF_GUIDED_WELLNESS_RESOURCES),
        testID: 'selfGuidedResources-label',
      },
      description: {
        text: formatMessage(lyraMemberOfferingsDescriptions.SELF_GUIDED_WELLNESS_RESOURCES),
        testID: 'selfGuidedResources-description',
      },
      icon: isAuroraTheme ? (
        <AuroraSelfCareResourcesSpotIllustration size={iconSize} />
      ) : (
        <SelfCareValuePropSpotIllustration size={iconSize} />
      ),
      hide: isLibraryDisabled,
      itemTestID: 'selfGuidedResources',
    },
    {
      label: { text: formatMessage(lyraMemberOfferingsLabels.DEDICATED_SUPPORT), testID: 'dedicatedSupport-label' },
      description: {
        text: formatMessage(lyraMemberOfferingsDescriptions.DEDICATED_SUPPORT),
        testID: 'dedicatedSupport-description',
      },
      icon: isAuroraTheme ? (
        <AuroraCareNavigatorSpotIllustration size={iconSize} />
      ) : (
        <CareNavigatorValuePropSpotIllustration size={iconSize} />
      ),
      itemTestID: 'dedicatedSupport',
    },
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels.CARE_MATCHED_TO_YOU),
        testID: 'careMatchedToYouSection-label',
      },
      description: {
        text: formatMessage(lyraMemberOfferingsDescriptions.CARE_MATCHED_TO_YOU),
        testID: 'careMatchedToYouSection-description',
      },
      icon: isAuroraTheme ? (
        <AuroraAppSpotIllustration size={iconSize} />
      ) : (
        <IntelligentMatchingValuePropSpotIllustration size={iconSize} />
      ),
      itemTestID: 'careMatchedToYouSection',
    },
  ]

  const lyraSelectMembershipOffering = [
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels[LyraMemberOfferings.LYRA_SELECT_SELF_GUIDED_WELLNESS_RESOURCES]),
        testID: 'lyraSelect-selfGuidedResources-label',
      },
      description: {
        text: formatMessage(lyraMemberOfferingsDescriptions.LYRA_SELECT_SELF_GUIDED_WELLNESS_RESOURCES),
        testID: 'lyraSelect-selfGuidedResources-description',
      },
      icon: <LyraSelectSelfCareValueSpotIllustration size={iconSize} />,
      hide: isLibraryDisabled,
      itemTestID: 'lyraSelect-selfGuidedResourcesSection',
    },
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels[LyraMemberOfferings.LYRA_SELECT_PERSONALIZED_CARE]),
        testID: 'lyraSelect-personalizedCare-label',
      },
      description: {
        text: formatMessage(lyraMemberOfferingsDescriptions[LyraMemberOfferings.LYRA_SELECT_PERSONALIZED_CARE]),
        testID: 'lyraSelect-personalizedCare-description',
      },
      icon: <LyraSelectPersonalizedCareIllustration size={iconSize} />,
      itemTestID: 'lyraSelect-personalizedCareSection',
    },
    {
      label: {
        text: formatMessage(
          lyraMemberOfferingsLabels[LyraMemberOfferings.LYRA_SELECT_ONE_ON_ONE_MENTAL_HEALTH_SESSIONS],
        ),
        testID: 'lyraSelect-careSection-label',
      },
      description: {
        text: (() => {
          if (isHideCostAndSessionLimitEnabled || isCustomerDynamicSessionLimitEnabled) {
            return genericSessionMessage
          }
          if (displayVisitsPerIssuePerYear) {
            return totalDisplayVisitsPerIssuePerYearMessage
          }

          // Handle per-issue-per-year patterns first
          if (hasTherapyPerIssuePerYear && hasCoachingPerIssuePerYear) {
            return bothPerIssuePerYearMessage
          } else if (hasTherapyPerIssuePerYear && numberOfCoachingSessions) {
            return therapyPerIssueCoachingPerYearMessage
          } else if (hasCoachingPerIssuePerYear && numberOfTherapySessions) {
            return coachingPerIssueTherapyPerYearMessage
          }

          // Fall back to existing logic for regular per-year sessions
          return numberOfSessionsByProgram && (numberOfCoachingSessions || numberOfTherapySessions)
            ? coachingAndTherapyCountMessage
            : totalNumberOfSessionMessage
        })(),
        testID: 'lyraSelect-careSection-description',
      },
      icon: <LyraSelectCareSessionIllustration size={iconSize} />,
      itemTestID: 'lyraSelect-careSectionSection',
    },
    {
      label: {
        text: formatMessage(lyraMemberOfferingsLabels[LyraMemberOfferings.LYRA_SELECT_DEDICATED_SUPPORT]),
        testID: 'lyraSelect-dedicatedSupport-label',
      },
      description: {
        text: formatMessage(lyraMemberOfferingsDescriptions[LyraMemberOfferings.LYRA_SELECT_DEDICATED_SUPPORT]),
        testID: 'lyraSelect-dedicatedSupport-description',
      },
      icon: <LyraSelectCareNavigatorValuePropSpotIllustration size={iconSize} />,
      itemTestID: 'lyraSelect-dedicatedSupportSection',
    },
  ]
  return (
    <MembershipSummary
      title={formatMessage(LYRA_MEMBERSHIP_SECTION_TITLE)}
      memberOfferings={isLyraSelect ? lyraSelectMembershipOffering : memberOfferings}
      backgroundColor={backgroundColor}
    />
  )
}
